import { Box, Tooltip } from '@material-ui/core'
import { ClosableDialog } from 'components/controls/dialogs'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { getGameCodeFromName } from 'lib/utils'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AutoSizer,
  Column,
  SortDirection,
  SortDirectionType,
  Table,
  TableRowRenderer,
} from 'react-virtualized'
import { GAME_NAMES } from 'shared'
import { TransactionLogInterface } from './InventoryTransactionsPresenter'
import { getErrorContent } from './InventoryTransactionsUtils'

interface QuantityTransactionsTableProps {
  transactions: TransactionLogInterface[]
}

export const QuantityTransactionsTable = ({
  transactions,
}: QuantityTransactionsTableProps): JSX.Element => {
  const { t } = useTranslation()
  const [openErrorTransactionDialog, setOpenErrorTransactionDialog] = useState(false)
  const [currentError, setCurrentError] = useState<any>(null)
  const [productInformations, setProductInformations] = useState<
    | {
        gameName: string
        mkmProductId: number
        channel: string
      }
    | undefined
  >(undefined)

  const [sortBy, setSortBy] = useState<string>('createdAt')
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.DESC)
  const sort = ({ sortBy }: { sortBy: string }) => {
    setSortBy(sortBy)
    setSortDirection(sortDirection === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC)
    let sortedOrders = [...transactions]

    if (sortBy === 'createdAt') {
      sortedOrders = transactions.sort((a, b) => {
        return a.createdAt < b.createdAt ? 1 : -1
      })
    } else if (sortBy === 'processedAt') {
      sortedOrders = transactions.sort((a, b) => {
        const from = a.processedAt ?? ''
        const to = b.processedAt ?? ''
        return from < to ? 1 : -1
      })
    }

    if (sortDirection === SortDirection.DESC) {
      sortedOrders = sortedOrders.reverse()
    }
    return sortedOrders
  }

  const getFormattedDate = (d: Date | undefined) => {
    if (d) {
      const localeDate = new Date(d).toLocaleString()
      const [date, time] = localeDate.split(', ')
      return (
        <>
          {date} - {time}
        </>
      )
    } else return <></>
  }

  const openErrorTransactionDialogWithContentInformation = async (transaction: any) => {
    setCurrentError(transaction.lastError)
    setProductInformations({
      gameName: GAME_NAMES[transaction.idGame],
      mkmProductId: transaction.mkmProductId,
      channel: transaction.channel,
    })
    setOpenErrorTransactionDialog(true)
  }

  const rowRenderer: TableRowRenderer = ({ index, key, style, columns, className }) => {
    index
    return (
      <div key={key} style={style} className={className}>
        {columns}
      </div>
    )
  }

  return (
    <div data-testid="quantity-transactions-table" className="transactions-table">
      <h5>{t('transaction.quantityTransactions')}</h5>
      <div className="mt-3 responsive-table-container" style={{ minWidth: 'fit-content' }}>
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table
              width={Math.max(width, 1300)}
              height={transactions.length * 40 + 40}
              headerHeight={40}
              rowHeight={40}
              rowCount={transactions.length}
              rowGetter={({ index }) => transactions[index]}
              rowRenderer={rowRenderer}
              overscanRowCount={5}
              sort={sort}
              sortBy={sortBy}
              sortDirection={sortDirection}
              className="picking-table"
            >
              {/* channel */}
              <Column
                width={150}
                disableSort={true}
                label={t('transaction.channel')}
                dataKey="channel"
                cellRenderer={({ rowData }) => <Box width="100px">{rowData.channel}</Box>}
              />

              {/* created at */}
              <Column
                width={150}
                label={t('transaction.createdAt')}
                dataKey="createdAt"
                cellRenderer={({ rowData }) => (
                  <Box className="text-wrap">{getFormattedDate(rowData.createdAt)}</Box>
                )}
              />

              {/* processed at */}
              <Column
                width={170}
                label={t('transaction.processedAt')}
                dataKey="processedAt"
                cellRenderer={({ rowData }) => (
                  <Box className="text-wrap">
                    {rowData.processedAt === null && rowData.lastFailedAt === null ? (
                      <div className="d-flex flex-row align-items-center">
                        <Tooltip title="Pending" placement="top">
                          <span className="processed-icon pending-icon">P</span>
                        </Tooltip>
                      </div>
                    ) : rowData.processedAt === null ? (
                      <div className="d-flex flex-row align-items-center">
                        <Tooltip title="Failed" placement="top">
                          <span className="processed-icon failed-icon">F</span>
                        </Tooltip>
                        {getFormattedDate(rowData.lastFailedAt)}
                      </div>
                    ) : (
                      <div className="d-flex flex-row align-items-center">
                        <Tooltip title="Success" placement="top">
                          <span className="processed-icon success-icon">S</span>
                        </Tooltip>
                        {getFormattedDate(rowData.processedAt)}
                      </div>
                    )}
                  </Box>
                )}
              />

              {/* last error */}
              <Column
                width={170}
                disableSort={true}
                label="Last error"
                dataKey="lastError"
                cellRenderer={({ rowData }) => (
                  <Box textAlign="center">
                    {rowData.lastError && (
                      <i
                        className="fa-regular fa-circle-question error-transactions-question"
                        onClick={() => openErrorTransactionDialogWithContentInformation(rowData)}
                      />
                    )}
                  </Box>
                )}
              />

              {/* cause */}
              <Column
                width={150}
                disableSort={true}
                label={t('transaction.cause')}
                dataKey="cause"
                cellRenderer={({ rowData }) => <Box>{rowData.cause}</Box>}
              />

              {/* source */}
              <Column
                width={150}
                disableSort={true}
                label={t('transaction.source')}
                dataKey="source"
                cellRenderer={({ rowData }) => <Box>{rowData.source}</Box>}
              />

              {/* game */}
              <Column
                width={60}
                disableSort={true}
                label=""
                dataKey="idGame"
                cellRenderer={({ rowData }) => (
                  <span
                    className={`ss-${getGameCodeFromName(
                      GAME_NAMES[rowData.idGame]
                    )} ss-game-icon ss-fw`}
                    title={GAME_NAMES[rowData.idGame]}
                    style={{ fontSize: '1.5rem' }}
                  />
                )}
              />

              {/* name */}
              <Column
                width={300}
                disableSort={true}
                label={t('card.name')}
                dataKey="name"
                cellRenderer={({ rowData }) => (
                  <Box
                    className="text-wrap text-clamp-2"
                    data-testid={rowData.name}
                    title={rowData.name}
                  >
                    {rowData.name}
                  </Box>
                )}
              />

              {/* cn - expansion */}
              <Column
                width={300}
                disableSort={true}
                label={t('orders.cnExpansion')}
                dataKey="expansionName"
                cellRenderer={({ rowData }) => (
                  <div className="d-flex flex-column">
                    <div className="text-truncate" data-testid={rowData.expansionCode}>
                      #{rowData.cn} - {rowData.expansionCode}
                    </div>
                    <div className="text-truncate" title={rowData.expansionName}>
                      {rowData.expansionName}
                    </div>
                  </div>
                )}
              />

              {/* properties */}
              <Column
                width={200}
                disableSort={true}
                label={t('transaction.properties')}
                dataKey="properties"
                cellRenderer={({ rowData }) => (
                  <Box>
                    <ArticleAttributes article={rowData} iconHeight={17} />
                  </Box>
                )}
              />

              {/* location */}
              <Column
                width={200}
                disableSort={true}
                label={t('card.location')}
                dataKey="location"
                cellRenderer={({ rowData }) => (
                  <div>
                    <Tooltip title={rowData.location ?? ''} placement="top">
                      <Box
                        width="220"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {rowData.location}
                      </Box>
                    </Tooltip>
                  </div>
                )}
              />

              {/* from - to */}
              <Column
                width={250}
                disableSort={true}
                label={t('transaction.fromTo')}
                headerClassName="centered-header"
                dataKey="fromTo"
                cellRenderer={({ rowData }) => (
                  <Box textAlign="center">
                    {rowData.quantityFrom} &rArr; {rowData.quantityTo} (
                    {rowData.deltaQuantity > 0 ? '+' : ''}
                    {rowData.deltaQuantity})
                  </Box>
                )}
              />
            </Table>
          )}
        </AutoSizer>
      </div>

      {currentError && (
        <ClosableDialog
          open={openErrorTransactionDialog}
          onClose={() => {
            setOpenErrorTransactionDialog(false)
            setCurrentError(undefined)
            setProductInformations(undefined)
          }}
        >
          <div className="p-5">
            {getErrorContent(
              currentError,
              productInformations!.gameName,
              productInformations!.mkmProductId,
              productInformations!.channel
            )}
          </div>
        </ClosableDialog>
      )}
    </div>
  )
}
